import { getFunctions, httpsCallable } from "firebase/functions";
import { normalizeEmail } from "../utils";
import { getUserLabel } from "../identity";

const EMAIL_SHAPED = /^[^@\s]+@[^@\s]+$/;
const EXPLICIT_NON_EMAIL_TYPES = new Set(["qsUsername", "discordUsername"]);

export async function emitNotificationEvent(event) {
  const functions = getFunctions();
  const emit = httpsCallable(functions, "emitNotificationEvent");
  const response = await emit(event);
  return response.data;
}

export function buildNotificationActor(user) {
  const u = user || {};
  // Only an EXPLICIT non-email type (qsUsername/discordUsername) withholds the
  // email. An absent/null type means DEFAULT EMAIL, so an untyped actor keeps
  // its email (matches buildPublicIdentifier's `type = ... || "email"`).
  const isExplicitNonEmail = EXPLICIT_NON_EMAIL_TYPES.has(u.publicIdentifierType);

  // displayName is rendered to OTHER users, so it must never be the email of a
  // user who EXPLICITLY chose a non-email identifier type. Route through the
  // identity helper and, as a guardrail, drop any email-shaped result for such
  // a user in favor of "Someone".
  let label = getUserLabel(u);
  if (isExplicitNonEmail && EMAIL_SHAPED.test(String(label || "").trim())) {
    label = "";
  }

  return {
    uid: u.uid || null,
    // Withhold the raw email only for explicit non-email types; untyped
    // (default-email) and email-type actors carry it.
    email: isExplicitNonEmail ? null : normalizeEmail(u.email) || null,
    displayName: label || "Someone",
    // Carry the chosen public identifier so downstream renders/resolvers can
    // honor it without reaching for the email.
    publicIdentifier: u.publicIdentifier || null,
    publicIdentifierType: u.publicIdentifierType || null,
    qsUsername: u.qsUsername || null,
    discordUsername: u.discordUsername || null,
  };
}

export async function emitPollEvent({
  eventType,
  schedulerId,
  pollTitle,
  actor,
  payload,
  recipients,
  dedupeKey,
}) {
  return emitNotificationEvent({
    eventType,
    resource: { type: "poll", id: schedulerId, title: pollTitle },
    actor,
    payload: { pollTitle, ...(payload || {}) },
    recipients,
    dedupeKey,
  });
}

export async function reconcilePendingNotifications() {
  const functions = getFunctions();
  const reconcile = httpsCallable(functions, "reconcilePendingNotifications");
  const response = await reconcile();
  return response.data;
}
